import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import FieldRenderer from "@/components/FieldRenderer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2 } from "lucide-react";

/**
 * Single-object profile form with autosave.
 * endpoint: PUT endpoint. wrap: send { data: values } if true, else flat.
 * loadKey: key in GET /profile response that holds the current values.
 */
export default function ProfileForm({ fields, endpoint, wrap = true, loadKey, groups, reloadToken }) {
  const [values, setValues] = useState({});
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/profile").then((res) => {
      const v = loadKey ? res.data[loadKey] || {} : res.data;
      setValues(v || {});
      setLoaded(true);
    });
  }, [loadKey, reloadToken]);

  const save = async (next) => {
    setStatus("saving");
    try {
      await api.put(endpoint, wrap ? { data: next } : next);
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 1500);
    } catch (e) {
      setStatus("idle");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const update = (name, val) => {
    const next = { ...values, [name]: val };
    setValues(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => save(next), 900);
  };

  if (!loaded) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" /></div>;

  const renderGroup = (fieldList) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {fieldList.map((f) => (
        <div key={f.name} className={f.full ? "md:col-span-2" : ""}>
          <FieldRenderer field={f} value={values[f.name]} onChange={(v) => update(f.name, v)} />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 stagger-in">
      {groups ? (
        groups.map((g) => (
          <Card key={g.title} className="rounded-2xl border-border p-6 md:p-8">
            <h3 className="font-head text-lg font-semibold mb-1">{g.title}</h3>
            {g.description && <p className="text-sm text-muted-foreground mb-5">{g.description}</p>}
            {renderGroup(g.fields)}
          </Card>
        ))
      ) : (
        <Card className="rounded-2xl border-border p-6 md:p-8">{renderGroup(fields)}</Card>
      )}
      <div className="flex items-center gap-3 justify-end">
        <span className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="autosave-status">
          {status === "saving" && (<><Loader2 className="h-4 w-4 animate-spin" /> Sparar…</>)}
          {status === "saved" && (<><Check className="h-4 w-4 text-[hsl(var(--success))]" /> Sparat automatiskt</>)}
        </span>
        <Button data-testid="save-now-btn" className="rounded-full" onClick={() => save(values)}>Spara nu</Button>
      </div>
    </div>
  );
}
